/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import useCustomerMgr from 'coreHooks/useCustomerMgr';
import {useMemo} from 'react';
import useCustomerProductLists from '../useCustomerProductLists';
import type {
	ICustomerProductListItemInput,
	IGetListOptions,
	IWishlistDataLayerOptions,
	TCustomerProductListItemUpdate,
	TSavedGuestItem,
} from '../useCustomerProductLists';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import ProductListItem from 'coreClasses/customer/productList/ProductListItem';
import type ProductModel from 'coreClasses/product/ProductModel';
import type {CustomerProductListItem, CustomerProductListState} from 'coreStore/data/customerProductLists/types';

import type {ICustomerProductList} from './IUseCustomerProductList';

/**
 * This hook is built on top of the useCustomerProductLists hook,
 * to provide functionalities to manage a single list.
 * A typical use case is wish list.
 */
const useCustomerProductList = (name?: string, type?: string): ICustomerProductList => {
	// cpl is the shorthand for "Customer Product Lists"
	const {managerRepository} = useGeneralProvider();
	const cpl = useCustomerProductLists();
	const customer = useCustomerMgr().getCurrentCustomer();

	const self = useMemo<ICustomerProductList>(() => {
		const getCustomerProductListProductIds = (): string[] => {
			return self.data?.customerProductListItems?.map((item) => item.productId) || [];
		};

		return {
			...cpl,

			get data(): CustomerProductListState | undefined {
				return name ? cpl.findListByName(name) : cpl.getFirstList(type);
			},

			get items(): CustomerProductListItem[] {
				return self.data?.customerProductListItems || [];
			},

			get isEmpty(): boolean {
				return !self.items?.length;
			},

			get isInitialized(): boolean {
				return !!self.productListId;
			},

			get productListId(): string | undefined {
				return self.data?.id;
			},

			// boolean value indicate the list
			// is populated with product details
			get hasDetail(): boolean {
				return !!self.data?.hasDetail;
			},

			// boolean value indicate the list is public,
			// i.e. readable by other shoppers
			get isPublic(): boolean {
				return !!self.data?.public;
			},

			/**
			 * Returns the product list Items
			 * @returns {ProductListItem[]} product list items
			 */
			getProductListItems(): ProductListItem[] {
				return self.items.map((item) => new ProductListItem(managerRepository, item));
			},

			/**
			 * Initialize the list.
			 * @param {IGetListOptions} [options] - initialization options
			 * @param {TSavedGuestItem[]} [savedGuestItems] - optional array of guest items to merge
			 * @returns {Promise<CustomerProductListState>} the customer product list
			 */
			init(options?: IGetListOptions, savedGuestItems?: TSavedGuestItem[]): Promise<CustomerProductListState> {
				return cpl.getOrCreateList(name, type, options, self.productListId, savedGuestItems);
			},

			/**
			 * Reset the list.
			 */
			reset(): Promise<void> {
				return cpl.reset();
			},

			/**
			 * Checks if the product list is ready to use
			 * @returns {boolean} is ready to use
			 */
			isReady(): boolean {
				if (!self.isInitialized) {
					return false;
				}

				const productListProductIds = getCustomerProductListProductIds();

				if (productListProductIds?.length === 0) {
					return true;
				}

				const products = managerRepository.getProductsMgr().getProducts(productListProductIds);

				return !products.some((product) => !product.isReady());
			},

			/**
			 * Fetch products in the product list
			 * @returns {Promise<ProductModel[]>} get products promise
			 */
			async fetchProductsInProductList(): Promise<ProductModel[]> {
				const productIds = getCustomerProductListProductIds();

				// getProducts returns the requested ids, not products, when none are requested
				if (!productIds.length) {
					return [];
				}

				return await managerRepository.getProductsMgr().getProducts(productIds) as ProductModel[];
			},

			/**
			 * Adds an item to the customer's list.
			 * @param {ICustomerProductListItemInput} item - item to be added to the list
			 * @param {IWishlistDataLayerOptions} [dataLayerObject] - data layer event options
			 * @param {string|null} [listItemID] - list to add the item to, the customer's list when omitted
			 * @returns {Promise<CustomerProductListItem>} the created item
			 */
			createListItem: async (
				item: ICustomerProductListItemInput,
				dataLayerObject: IWishlistDataLayerOptions = {},
				listItemID?: string | null,
			): Promise<CustomerProductListItem> => {
				if (!self.isInitialized) {
					const list = await self.init();

					return cpl.createListItem(list.id, item, dataLayerObject);
				}

				return cpl.createListItem(listItemID || self.productListId, item, dataLayerObject);
			},

			/**
			 * Set the visibility of the customer's list.
			 * @param {boolean} isPublic - true to make the list readable by other shoppers
			 * @returns {Promise<CustomerProductListState>} updated customer product list
			 */
			setPublic: async (isPublic: boolean): Promise<CustomerProductListState> => {
				if (!self.isInitialized) {
					const list = await self.init();

					return cpl.setListPublic(list.id, isPublic);
				}

				return cpl.setListPublic(self.productListId, isPublic);
			},

			/**
			 * Update an item to the customer's list.
			 * @param {TCustomerProductListItemUpdate} item - item to be updated
			 * @returns {Promise<CustomerProductListItem | undefined>} the updated item, undefined when it has been removed (quantity 0)
			 */
			updateListItem: (item: TCustomerProductListItemUpdate): Promise<CustomerProductListItem | undefined> => {
				return cpl.updateListItem(self.productListId, item);
			},

			/**
			 * Remove an item from a customer product list
			 * @param {string} itemId - the id of the item in the product list
			 * @returns {Promise<void>} remove item from list
			 */
			removeListItem: (itemId: string): Promise<void> => {
				return cpl.removeListItem(self.productListId, itemId);
			},

			/**
			 * Remove an item from the customer's list.
			 * @param {string} productId - the id of the product
			 * @returns {Promise<void> | undefined} remove the item from list, undefined when the product is not in the list
			 */
			removeListItemByProductId: (productId: string): Promise<void> | undefined => {
				return cpl.removeListItemByProductId(self.productListId, productId);
			},

			/**
			 * Remove items from the customer's list retrieving them by variation group id.
			 * @param {string[]} variationGroupIds - Variation groups to remove from list
			 * @returns {Promise<void>} remove items from list
			 */
			removeListItemsByVariationGroupIds: (variationGroupIds: string[]): Promise<void> => {
				return cpl.removeListItemsByVariationGroupIds(self.productListId, variationGroupIds);
			},

			/**
			 * Find the item from list.
			 * @param {string} productId - the id of the product
			 * @returns {CustomerProductListItem | undefined} product list item
			 */
			findItemByProductId(productId: string): CustomerProductListItem | undefined {
				if (!self.isInitialized) {
					return undefined;
				}

				return cpl.findItemByProductId(self.productListId, productId);
			},

			/**
			 * Find the items from list by variation group.
			 * @param {string} variationGroupId - the variation group id
			 * @returns {CustomerProductListItem[]} array of product list item
			 */
			findItemsByVariationGroupId(variationGroupId: string): CustomerProductListItem[] {
				if (!self.isInitialized) {
					return [];
				}

				return cpl.findItemsByVariationGroupId(self.productListId, variationGroupId);
			},
		};
	}, [cpl, customer.isRegistered, customer.siteId]);

	return self;
};

export default useCustomerProductList;
